import React from 'react';
import AuditLogRow from './AuditLogRow';
import LoadingSpinner from './LoadingSpinner';

/**
 * AuditLogTable Component
 * 
 * Renders the audit logs table with headers, data rows, and various states.
 * Replaces legacy table implementation with OM Design System components while
 * preserving exact same table structure and data display behavior.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.auditLogs - Array of audit log entries
 * @param {boolean} props.loading - Whether data is loading
 * @param {Object} props.pagination - Pagination state object (for display info only)
 * @param {number} props.pagination.total - Total number of items
 */
const AuditLogTable = ({
  auditLogs = [],
  loading = false,
  pagination = { total: 0 }
}) => {
  /**
   * Render loading spinner
   * Uses OM Design System LoadingSpinner component
   */
  const renderLoadingSpinner = () => (
    <LoadingSpinner />
  );

  /**
   * Render empty state
   * Shows when no audit logs match the current criteria
   */
  const renderEmptyState = () => (
    <div className="px-6 py-8 text-center">
      <p className="text-gray-500">No audit logs found matching your criteria.</p>
    </div>
  );

  /**
   * Render table headers
   * Maintains exact same column structure as legacy component
   */
  const renderTableHeaders = () => (
    <thead className="bg-gray-50">
      <tr>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Timestamp
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          User
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Action
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Claim ID
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Details
        </th>
      </tr>
    </thead>
  );

  /**
   * Render table body with audit log rows
   */
  const renderTableBody = () => (
    <tbody className="bg-white divide-y divide-gray-200">
      {auditLogs.map((log) => (
        <AuditLogRow key={log.id} log={log} />
      ))}
    </tbody>
  );



  // Show loading spinner if loading and no data
  if (loading && auditLogs.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Audit Log Entries
          </h2>
        </div>
        {renderLoadingSpinner()}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Table Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">
          Audit Log Entries
          {pagination.total > 0 && (
            <span className="text-sm text-gray-500 ml-2">
              ({pagination.total} total)
            </span>
          )}
        </h2>
      </div>

      {/* Table Content */}
      {auditLogs.length === 0 ? (
        renderEmptyState()
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              {renderTableHeaders()}
              {renderTableBody()}
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default AuditLogTable;